'use client';

/**
 * Thin client wrappers for integration-page analytics. The visual chrome
 * comes from the V4 cyan-pillar primitives in `src/components/landing/v4-shared.tsx`
 * — these wrappers exist only so the parent RSC can fire PostHog events
 * on click without making the whole page client-side.
 */

import Link from 'next/link';
import type { CSSProperties, ReactNode } from 'react';
import { pushEvent } from '@/lib/analytics/core';

interface TrackedSignupCtaProps {
  partner: string;
  size?: 'md' | 'lg';
  children: ReactNode;
}

/**
 * Cyan-gradient primary CTA that fires `integration_cta_click` on click
 * and forwards the partner slug as a query param so the onboarding wizard
 * can pre-select the matching integration.
 */
export function IntegrationSignupCta({
  partner,
  size = 'md',
  children,
}: TrackedSignupCtaProps) {
  const sizing =
    size === 'lg'
      ? { padding: '18px 34px', fontSize: '16px' }
      : { padding: '14px 26px', fontSize: '15px' };
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'var(--font-geist-sans)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: 'var(--ink)',
    background:
      'linear-gradient(135deg, var(--cyan-glow) 0%, var(--paper) 100%)',
    borderRadius: '999px',
    border: '1px solid rgba(125,250,255,0.4)',
    boxShadow: '0 10px 30px -10px rgba(34,211,238,0.55)',
    textDecoration: 'none',
    cursor: 'pointer',
    ...sizing,
  };
  return (
    <Link
      href={`/auth/register?integration=${encodeURIComponent(partner)}`}
      onClick={() =>
        pushEvent('integration_cta_click', { partner, cta_type: 'signup' })
      }
      className="v4-cta-primary"
      style={style}
    >
      {children}
    </Link>
  );
}

interface VisitPartnerProps {
  partner: string;
  partnerName: string;
  href: string;
  size?: 'md' | 'lg';
  children: ReactNode;
}

export function IntegrationVisitPartnerCta({
  partner,
  partnerName,
  href,
  size = 'md',
  children,
}: VisitPartnerProps) {
  const sizing =
    size === 'lg'
      ? { padding: '18px 32px', fontSize: '16px' }
      : { padding: '14px 24px', fontSize: '15px' };
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'var(--font-geist-sans)',
    fontWeight: 500,
    letterSpacing: '-0.01em',
    color: 'var(--cyan-glow)',
    background: 'transparent',
    border: '1px solid rgba(125,250,255,0.4)',
    borderRadius: '999px',
    textDecoration: 'none',
    cursor: 'pointer',
    ...sizing,
  };
  return (
    <a
      href={href}
      rel="noopener"
      onClick={() =>
        pushEvent('integration_cta_click', {
          partner,
          cta_type: 'visit_partner',
          partner_name: partnerName,
        })
      }
      className="v4-cta-ghost v4-cta-ghost--cyan"
      style={style}
    >
      {children}
    </a>
  );
}
